import { addDays, addMinutes, isSameDay, startOfDay } from "date-fns";
import type { Appointment, AppointmentStatus } from "../calendar/types";

export type CardDensity = "past" | "active" | "upcoming";

export function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

export function fmtTime(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function diffMinutes(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / 60_000);
}

export function isPastStatus(s: AppointmentStatus): boolean {
  return s === "completed" || s === "cancelled" || s === "no_show";
}

/**
 * Determine card density.
 * - PAST: completed/cancelled/no_show OR (anything that ended before now)
 * - ACTIVE: in_progress OR the nearest upcoming within 15 min
 * - UPCOMING: everything else
 */
export function deriveDensity(
  appt: Appointment,
  now: Date,
  isNearestUpcoming: boolean,
): CardDensity {
  if (isPastStatus(appt.status)) return "past";
  if (appt.endTime.getTime() <= now.getTime()) return "past";
  if (appt.status === "in_progress") return "active";
  const minutesUntil = diffMinutes(appt.startTime, now);
  if (isNearestUpcoming && minutesUntil <= 15 && minutesUntil >= -5) return "active";
  return "upcoming";
}

export function sortByStart(list: Appointment[]): Appointment[] {
  return [...list].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

/**
 * Find the next "actionable" appointment id — first confirmed/pending whose
 * start is >= now (or whose start was recent enough to still be "next").
 * If nothing actionable, returns null.
 */
export function findNextActionable(
  list: Appointment[],
  now: Date,
): Appointment | null {
  const sorted = sortByStart(list);
  // Prefer in_progress (it's literally "now")
  const inProgress = sorted.find((a) => a.status === "in_progress");
  if (inProgress) return inProgress;
  const upcoming = sorted.find(
    (a) =>
      (a.status === "confirmed" || a.status === "pending") &&
      a.endTime.getTime() > now.getTime(),
  );
  return upcoming ?? null;
}

/** Group appointments by hour (HH integer). */
export function groupByHour(list: Appointment[]): Map<number, Appointment[]> {
  const sorted = sortByStart(list);
  const map = new Map<number, Appointment[]>();
  for (const a of sorted) {
    const h = a.startTime.getHours();
    if (!map.has(h)) map.set(h, []);
    map.get(h)!.push(a);
  }
  return map;
}

export function filterToDay(list: Appointment[], date: Date): Appointment[] {
  return list.filter((a) => isSameDay(a.startTime, date));
}

export function applySegment(
  list: Appointment[],
  segment: "all" | "active" | "done",
  now: Date,
): Appointment[] {
  if (segment === "all") return list;
  if (segment === "done") return list.filter((a) => isPastStatus(a.status) || a.endTime.getTime() <= now.getTime());
  // active = anything not past
  return list.filter((a) => !isPastStatus(a.status) && a.endTime.getTime() > now.getTime());
}

export function durationMin(a: Appointment): number {
  return Math.max(15, Math.round((a.endTime.getTime() - a.startTime.getTime()) / 60_000));
}

export function formatDayHeader(
  date: Date,
  weekdays: readonly string[],
  months: readonly string[],
): string {
  const wd = weekdays[(date.getDay() + 6) % 7];
  return `${wd}, ${date.getDate()} ${months[date.getMonth()]}`;
}

export { addDays, addMinutes, isSameDay, startOfDay };

/** Trigger a haptic pulse if the platform supports it. */
export function haptic(kind: "select" | "success" | "warn" | "error"): void {
  if (typeof navigator === "undefined" || !("vibrate" in navigator)) return;
  const map: Record<typeof kind, number | number[]> = {
    select: 10,
    success: [10, 30, 10],
    warn: [20, 40, 20],
    error: [40, 60, 40],
  };
  try {
    (navigator as Navigator).vibrate?.(map[kind]);
  } catch {
    /* noop */
  }
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}
